/*
 * Goro's Stack Builder — <nmkj-goros-stack>. Loaded ONLY by sections/nmkj-goros-stack-builder.liquid.
 *
 * What may go where lives in nmkj-goros-stack-rules.js (a held copy of the inventory-watch
 * module — never edit it here). This file is the page: feed, state, slots, picker,
 * availability, add-to-cart, and the "tell me when one arrives" line.
 *
 * State (localStorage `nmkj-goros-stack`) holds {sku,title} pairs per slot, never products:
 * the feed is re-read on load, on returning to the tab and before adding to the cart, and a
 * piece that has gone stays in its slot as a tombstone ("Just sold") instead of vanishing.
 */
const STORAGE_KEY = 'nmkj-goros-stack';
const FEED_TTL = 5 * 60 * 1000;
const COND = { N: 'Brand New', S: 'Like New', A: 'Excellent', B: 'Good', C: 'Fair' };
const COND_RANK = { 'Brand New': 4, 'Like New': 3, Excellent: 2, Good: 1 };
const WANT_ROLE = { base: 'chain', hook: 'hook', wheel: 'wheel', feather_l: 'feather', feather_r: 'feather', centre: 'medallion', beads: 'bead' };
// Wording stays clear of the two unconfirmed PHYSICAL claims in the rules (hangersNeedBase,
// hookClosesChain): these say how a set-up is usually worn, never what it needs.
const SLOT_HINT = {
  base: 'The chain with its hook — the base the other pieces are worn on.',
  hook: 'A hook for the chain you already own.',
  wheel: 'Worn on the chain, usually at the front.',
  feather_l: 'Worn to the left of the centre.',
  feather_r: 'Worn to the right of the centre.',
  centre: 'A medallion or pendant between the feathers.',
  beads: 'Up to two, worn along the chain.',
};

// Analytics (stage 8): GA4 only, straight through gtag. The Google & YouTube channel pixel
// already injects gtag.js and emits the standard ecommerce events (view_item, add_to_cart,
// purchase …) itself, so these are our four custom names and nothing standard. `send_to`
// pins them to the GA4 property; a page without gtag.js just accumulates dataLayer pushes.
const GA4_ID = 'G-T9K9L56XLL';
function track(name, params) {
  try {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('event', name, { ...params, send_to: GA4_ID });
  } catch { /* analytics never breaks the page */ }
}

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const lower = (s) => String(s).toLowerCase();

// Same approach as sections/nmkj-brand-row.liquid: products.json answers in the visitor's
// presentment currency and the page carries its code. Whole amounts without ".00", like the cards.
function money(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  const code = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(n).replace(/\.00$/, '');
  } catch {
    return (code === 'USD' ? '$' : code + ' ') + n.toLocaleString('en-US');
  }
}
function locale() {
  const l = (window.Shopify && Shopify.locale) || navigator.language || 'en';
  return lower(l).indexOf('ja') === 0 ? 'ja' : 'en';
}
function tagValue(tags, key) {
  const t = tags.find((x) => x.startsWith(key + ':'));
  return t ? t.slice(key.length + 1).trim() : '';
}
function condition(tags) {
  const v = tagValue(tags, 'Cond') || (tags.find((t) => /^Condition\s/i.test(t)) || '').replace(/^Condition\s*/i, '');
  return COND[v] || (COND_RANK[v] ? v : '');
}

class NmkjGorosStack extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    try {
      this.R = await import(this.dataset.rules);
    } catch (e) {
      this.innerHTML = `<p class="t-body-sm">The builder could not load. <a href="${esc(this.dataset.gorosUrl)}">Browse every Goro's piece</a> instead.</p>`;
      return;
    }
    this.events = import(this.dataset.events).catch(() => null);
    this.feed = new Map(); // sku → piece (feed fields + readPiece fields)
    this.seen = new Set(); // skus seen available this session — "just sold" vs "no longer available"
    this.gone = new Map(); // sku → 'sold' | 'missing'
    this.cartIds = new Set();
    this.status = null;
    this.entry = null;
    this.state = this.loadState();
    this.innerHTML = `
      <div data-gsb-root></div>
      <dialog class="nmkj-gsb__dialog" data-gsb-dialog aria-labelledby="nmkj-gsb-dialog-title"></dialog>
      <p class="visually-hidden" aria-live="polite" aria-atomic="true" data-gsb-live></p>`;
    this.root = this.querySelector('[data-gsb-root]');
    this.dialog = this.querySelector('[data-gsb-dialog]');
    this.live = this.querySelector('[data-gsb-live]');
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('submit', (e) => this.onSubmit(e));
    this.dialog.addEventListener('close', () => this.onDialogClose());
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.refresh().then(() => this.render()); });

    const cached = this.readCache();
    if (cached) { this.applyFeed(cached); this.render(); }
    await Promise.all([this.refresh(), this.loadCart()]);
    this.applyEntry();
    this.render();
  }

  // ── State ──────────────────────────────────────────────────────────────────────────────
  emptyState() {
    const slots = {};
    for (const s of this.R.STACK_SLOTS) slots[s.id] = [];
    return { v: 1, ownedBase: false, slots, updatedAt: 0 };
  }
  loadState() {
    const st = this.emptyState();
    try {
      const j = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (j && j.v === 1) {
        st.ownedBase = Boolean(j.ownedBase);
        for (const id of Object.keys(st.slots)) {
          st.slots[id] = (Array.isArray(j.slots?.[id]) ? j.slots[id] : [])
            .filter((e) => e && e.sku).map((e) => ({ sku: String(e.sku), title: String(e.title || '') }));
        }
      }
    } catch { /* fresh */ }
    return st;
  }
  save() {
    this.state.updatedAt = Date.now();
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)); } catch { /* private mode */ }
  }
  entries() { return Object.entries(this.state.slots).flatMap(([slot, list]) => list.map((e) => ({ slot, ...e }))); }
  chosen() { return this.entries().map((e) => ({ ...e, piece: this.feed.get(e.sku) })).filter((e) => e.piece && e.piece.available && !this.gone.has(e.sku)); }
  /** The stack as the rules see it: only live pieces. `omit` drops one sku (a swap) or empties a slot. */
  stackFor(omitSku) {
    const slots = {};
    for (const s of this.R.STACK_SLOTS) {
      slots[s.id] = this.state.slots[s.id].filter((e) => e.sku !== omitSku).map((e) => this.feed.get(e.sku)).filter((p) => p && p.available && !this.gone.has(p.sku));
    }
    return { ownedBase: this.state.ownedBase, slots };
  }

  // ── Feed ───────────────────────────────────────────────────────────────────────────────
  readCache() {
    try {
      const j = JSON.parse(sessionStorage.getItem('nmkj-goros-feed:' + this.dataset.feed) || 'null');
      return j && Date.now() - j.t < FEED_TTL && Array.isArray(j.items) ? j.items : null;
    } catch { return null; }
  }
  async fetchFeed() {
    const base = this.dataset.feed;
    const out = [];
    for (let page = 1; page <= 4; page += 1) {
      const r = await fetch(`${base}&page=${page}&_=${Date.now()}`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('feed ' + r.status);
      const j = await r.json();
      const products = Array.isArray(j.products) ? j.products : [];
      for (const p of products) {
        const v = p.variants && p.variants[0];
        if (!v || !v.sku) continue;
        const tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map((t) => t.trim());
        const mats = tags.filter((t) => t.startsWith('Mat:')).map((t) => t.slice(4));
        const bare = tags.filter((t) => /^(Silver|Gold|White Gold)$/i.test(t));
        out.push({
          sku: String(v.sku), title: p.title, productType: p.product_type, vendor: p.vendor,
          metafields: { material: mats.length ? mats : bare, size: tagValue(tags, 'Size') },
          variantId: v.id, available: Boolean(v.available), price: Number(v.price),
          image: p.images && p.images[0] ? p.images[0].src : '', handle: p.handle, url: '/products/' + p.handle,
          tags, publishedAt: p.published_at || '', cond: condition(tags),
        });
      }
      if (products.length < 250) break;
    }
    try { sessionStorage.setItem('nmkj-goros-feed:' + base, JSON.stringify({ t: Date.now(), items: out })); } catch { /* full */ }
    return out;
  }
  applyFeed(items) {
    const R = this.R;
    this.feed = new Map();
    for (const it of items) {
      const read = R.readPiece(it);
      if (!read || !R.inStack(read)) continue; // rings, bracelets, earrings and non-Goro's never enter
      this.feed.set(it.sku, { ...it, ...read });
    }
    for (const e of this.entries()) {
      const p = this.feed.get(e.sku);
      if (p && p.available) { this.gone.delete(e.sku); this.seen.add(e.sku); if (!e.title) e.title = p.title; }
      else if (!this.gone.has(e.sku)) this.gone.set(e.sku, this.seen.has(e.sku) ? 'sold' : 'missing');
    }
    for (const p of this.feed.values()) if (p.available) this.seen.add(p.sku);
  }
  async refresh() {
    try { this.applyFeed(await this.fetchFeed()); this.feedError = false; }
    catch { this.feedError = true; }
  }
  async loadCart() {
    try {
      const r = await fetch(this.dataset.cartJson + '?_=' + Date.now(), { credentials: 'same-origin' });
      const j = await r.json();
      this.cartIds = new Set((j.items || []).map((i) => Number(i.variant_id)));
      this.cartCount = Number(j.item_count) || 0;
    } catch { /* keep the last known cart */ }
  }

  // ── Entry: ?sku=NNNNN ──────────────────────────────────────────────────────────────────
  applyEntry() {
    const params = new URLSearchParams(location.search);
    const sku = params.get('sku');
    const open = { source: sku ? 'pdp' : params.get('from') === 'collection' ? 'collection' : 'page' };
    if (sku) open.preselected_sku = sku;
    track('stack_builder_open', open);
    if (!sku && !params.has('from')) return;
    const url = new URL(location.href); url.searchParams.delete('sku'); url.searchParams.delete('from');
    history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
    if (!sku) return;
    const p = this.feed.get(sku);
    if (!p || !p.available) { this.setStatus('That piece is no longer available, so we could not add it.', 'warn'); return; }
    const slotId = this.R.slotFor(p);
    if (!slotId) { this.entry = { sku, sides: this.R.slotsFor(p) }; return; } // unsided feather: offer left or right
    this.placeFrom(slotId, sku, true);
    this.scrollToNextOpen();
  }
  placeFrom(slotId, sku, replaceFull) {
    const p = this.feed.get(sku);
    const slot = this.R.STACK_SLOTS.find((s) => s.id === slotId);
    if (this.entries().some((e) => e.sku === sku)) return true;
    if (replaceFull && this.state.slots[slotId].length >= slot.max) this.state.slots[slotId] = [];
    if (slotId === 'hook' && !this.state.ownedBase && this.state.slots.base.length === 0) this.state.ownedBase = true;
    const a = this.R.allowed(this.stackFor(), p, slotId);
    if (!a.ok) { this.setStatus(a.why, 'warn'); return false; }
    this.state.slots[slotId].push({ sku, title: p.title });
    this.save();
    track('stack_slot_fill', { role: p.primary, slot: slotId, mode: 'auto', sku });
    this.announce(`${p.title} added as ${slot.label.toLowerCase()}.`);
    return true;
  }

  // ── Slots ──────────────────────────────────────────────────────────────────────────────
  visibleSlots() {
    const hasHook = this.chosen().some((e) => e.slot !== 'hook' && e.piece.roles.includes('hook'));
    return this.R.STACK_SLOTS.filter((s) => s.id !== 'hook' || (this.state.ownedBase && !hasHook));
  }
  candidates(slotId, omitSku) {
    const stack = this.stackFor(omitSku);
    const out = [];
    for (const p of this.feed.values()) {
      if (!p.available || !this.R.allowed(stack, p, slotId).ok) continue;
      const f = this.R.fit(stack, p, slotId);
      out.push({ p, score: f.score, reasons: f.reasons.filter((r) => r !== 'Side not recorded').slice(0, 2) });
    }
    out.sort((a, b) => (b.score - a.score) || ((COND_RANK[b.p.cond] || 0) - (COND_RANK[a.p.cond] || 0))
      || (b.p.publishedAt > a.p.publishedAt ? 1 : b.p.publishedAt < a.p.publishedAt ? -1 : 0)
      || (a.p.price - b.p.price) || (a.p.sku < b.p.sku ? -1 : 1));
    return out;
  }
  scrollToNextOpen() {
    requestAnimationFrame(() => {
      const btn = this.root.querySelector('.nmkj-gsb__add');
      if (btn) btn.closest('.nmkj-gsb__slot').scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  // ── Render ─────────────────────────────────────────────────────────────────────────────
  render() {
    const R = this.R;
    const slots = this.visibleSlots();
    const chosen = this.chosen();
    const total = chosen.reduce((s, e) => s + e.piece.price, 0);
    const addable = chosen.filter((e) => !this.cartIds.has(e.piece.variantId));
    const goneCount = this.entries().filter((e) => this.gone.has(e.sku)).length;
    const addLabel = this.dataset.addLabel || 'Add all to cart';
    let entry = '';
    if (this.entry) {
      const p = this.feed.get(this.entry.sku);
      entry = p ? `<div class="nmkj-gsb__entry" data-gsb-entry><p><strong>${esc(p.title)}</strong> — side not recorded. Which side will you wear it on?</p>
        <div class="nmkj-gsb__actions">${this.entry.sides.map((id) => `<button type="button" class="nmkj-gsb__btn" data-act="place" data-slot="${id}" data-sku="${esc(p.sku)}">${esc(R.STACK_SLOTS.find((s) => s.id === id).label)}</button>`).join('')}
        <button type="button" class="nmkj-gsb__btn--link nmkj-gsb__btn" data-act="entry-dismiss">Not now</button></div></div>` : '';
    }
    this.root.innerHTML = `<div class="nmkj-gsb__layout">
      <div>${entry}<ol class="nmkj-gsb__slots">${slots.map((s, i) => this.slotHTML(s, i + 1)).join('')}</ol></div>
      <aside class="nmkj-gsb__summary" aria-label="Your set-up">
        <p class="nmkj-gsb__total"><span>Total</span><strong>${chosen.length ? money(total) : '—'}</strong></p>
        <p class="nmkj-gsb__count">${chosen.length} ${chosen.length === 1 ? 'piece' : 'pieces'} chosen${goneCount ? ` · ${goneCount} no longer available` : ''}</p>
        <p class="nmkj-gsb__one">Selecting a piece does not reserve it — every piece is one of one.</p>
        <button type="button" class="nmkj-gsb__btn nmkj-gsb__btn--primary" data-act="add-all" ${addable.length ? '' : 'disabled'}>${esc(addLabel)}${addable.length ? ` (${addable.length})` : ''}</button>
        ${this.pendingRemaining ? `<button type="button" class="nmkj-gsb__btn nmkj-gsb__btn--primary" style="margin-top:8px" data-act="add-remaining">Add the remaining ${this.pendingRemaining} ${this.pendingRemaining === 1 ? 'piece' : 'pieces'}</button>` : ''}
        <p class="nmkj-gsb__status${this.status && this.status.kind === 'warn' ? ' nmkj-gsb__status--warn' : ''}" data-gsb-status>${this.status ? esc(this.status.text) : ''}</p>
        <p class="nmkj-gsb__summary-links"><a href="${esc(this.dataset.cartUrl)}">View cart</a> · <a href="${esc(this.dataset.gorosUrl)}">All Goro's</a></p>
      </aside></div>${this.barHTML(chosen, total, addable, goneCount)}`;
  }
  /**
   * The phone's action bar: the total and the add button, within thumb reach. The summary card
   * is 1,500px down a six-slot page on a 390px screen, so without this the total and the add
   * button are effectively unreachable. CSS hides it from 900px up, where the card sticks beside
   * the slots instead. The buttons carry the SAME data-act values as the card's — onClick is
   * delegated and addAll() disables every matching button, so there is no second code path.
   * It carries the one-of-one line and any warning itself: from here a customer can add without
   * ever seeing the card.
   */
  barHTML(chosen, total, addable, goneCount) {
    if (!chosen.length && !this.pendingRemaining && !goneCount) return '';
    const addLabel = this.dataset.addLabel || 'Add all to cart';
    const warn = this.status && this.status.kind === 'warn' ? this.status.text : '';
    return `<div class="nmkj-gsb__bar" data-gsb-bar>
      ${warn ? `<p class="nmkj-gsb__bar-warn">${esc(warn)}</p>` : ''}
      <div class="nmkj-gsb__bar-row">
        <div class="nmkj-gsb__bar-figures">
          <p class="nmkj-gsb__bar-total">${chosen.length ? money(total) : '—'}</p>
          <p class="nmkj-gsb__bar-count">${chosen.length} ${chosen.length === 1 ? 'piece' : 'pieces'}${goneCount ? ` · ${goneCount} gone` : ''}</p>
        </div>
        <button type="button" class="nmkj-gsb__btn nmkj-gsb__btn--primary nmkj-gsb__bar-btn" data-act="add-all" ${addable.length ? '' : 'disabled'}>${esc(addLabel)}${addable.length ? ` (${addable.length})` : ''}</button>
      </div>
      ${this.pendingRemaining ? `<button type="button" class="nmkj-gsb__btn nmkj-gsb__btn--primary nmkj-gsb__bar-remaining" data-act="add-remaining">Add the remaining ${this.pendingRemaining} ${this.pendingRemaining === 1 ? 'piece' : 'pieces'}</button>` : ''}
      <p class="nmkj-gsb__bar-one">Not reserved — every piece is one of one.</p></div>`;
  }
  slotHTML(slot, n) {
    const list = this.state.slots[slot.id];
    const own = slot.ownable ? `<label class="nmkj-gsb__own"><input type="checkbox" data-act="owned-base" ${this.state.ownedBase ? 'checked' : ''}> I already own a chain &amp; hook</label>` : '';
    let body = list.map((e) => this.pieceHTML(slot, e)).join('');
    const canAdd = !(slot.ownable && this.state.ownedBase) && list.length < slot.max;
    if (canAdd) {
      const has = this.candidates(slot.id).length > 0;
      body += has
        ? `<p><button type="button" class="nmkj-gsb__add" data-act="open" data-slot="${slot.id}">Add a compatible ${esc(slot.label.toLowerCase())}</button></p>`
        : this.emptySlotHTML(slot);
    }
    if (slot.ownable && this.state.ownedBase) body = `<p class="nmkj-gsb__empty-note">Using your own chain &amp; hook.</p>`;
    return `<li class="nmkj-gsb__slot" data-slot="${slot.id}"><div class="nmkj-gsb__slot-head"><span class="nmkj-gsb__slot-n">${String(n).padStart(2, '0')}</span><h2 class="nmkj-gsb__slot-title">${esc(slot.label)}</h2></div>
      <p class="nmkj-gsb__slot-hint">${esc(SLOT_HINT[slot.id] || '')}</p>${own}${body}</li>`;
  }
  /**
   * A slot with no candidates. Two very different reasons, and saying the wrong one misleads:
   * the shop may genuinely have none in stock, OR the set-up already rules them out (a base that
   * carries its own wheel leaves every wheel ineligible). Only the first is worth watching for —
   * offering "tell me when a wheel arrives" to someone who already has one on their chain would
   * bank a notification they can never use.
   */
  emptySlotHTML(slot) {
    const stack = this.stackFor();
    let blocked = null;
    // Ask the stack first, not the shelf. A chosen base that carries this slot's role closes the
    // slot whether or not anything is in stock — and when the shelf is ALSO empty (every piece is
    // one of one, so wheels do drain) a stock-only test would find nothing to reject and fall back
    // to "none in stock", re-inviting the very watch this is here to prevent.
    const base = (stack.slots.base || [])[0];
    if (base) {
      const dup = slot.roles.find((r) => r !== 'chain' && base.roles.includes(r));
      if (dup) blocked = `Your chain & hook set already includes a ${dup}.`;
    }
    for (const p of this.feed.values()) {
      if (blocked) break;
      if (!p.available || !this.R.slotsFor(p).includes(slot.id)) continue;
      const a = this.R.allowed(stack, p, slot.id);
      if (!a.ok) { blocked = a.why; break; }
    }
    return blocked
      ? `<p class="nmkj-gsb__empty-note">${esc(blocked)}</p>`
      : `<p class="nmkj-gsb__empty-note">Nothing suitable is in stock right now.</p>${this.watchHTML(slot.id, 'slot')}`;
  }
  pieceHTML(slot, e) {
    const p = this.feed.get(e.sku);
    const gone = this.gone.get(e.sku);
    const title = (p && p.title) || e.title || e.sku;
    const img = p && p.image ? `<img src="${esc(p.image)}${p.image.includes('?') ? '&' : '?'}width=400" alt="" loading="lazy" width="400" height="400">` : '';
    const rmv = `<button type="button" class="nmkj-gsb__btn" data-act="remove" data-slot="${slot.id}" data-sku="${esc(e.sku)}">Remove</button>`;
    if (gone || !p) {
      const label = gone === 'sold' ? 'Just sold' : 'No longer available';
      return `<article class="nmkj-gsb__piece nmkj-gsb__piece--gone"><span class="nmkj-gsb__piece-img">${img}</span><div>
        <p class="nmkj-gsb__piece-title">${esc(title)}</p><p class="nmkj-gsb__gone">${label}</p>
        <div class="nmkj-gsb__actions"><button type="button" class="nmkj-gsb__btn" data-act="open" data-slot="${slot.id}" data-swap="${esc(e.sku)}">Replace</button>${rmv}</div>
        ${this.watchHTML(slot.id, 'gone')}</div></article>`;
    }
    const fit = this.R.fit(this.stackFor(e.sku), p, slot.id);
    const reasons = fit.reasons.slice(0, 2);
    if (p.primary === 'feather' && !p.side && !reasons.includes('Side not recorded')) reasons.unshift('Side not recorded');
    const meta = window.nmkjCardMeta ? window.nmkjCardMeta(p.tags, '', true) : '';
    const inCart = this.cartIds.has(p.variantId);
    return `<article class="nmkj-gsb__piece"><a class="nmkj-gsb__piece-img" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${img}</a><div>
      <a class="nmkj-gsb__piece-title" href="${esc(p.url)}">${esc(p.title)}</a>${meta}
      <p class="nmkj-gsb__piece-price">${money(p.price)}</p>
      ${reasons.length ? `<p class="nmkj-gsb__reasons">${esc(reasons.slice(0, 2).join(' · '))}</p>` : ''}
      ${inCart ? '<p class="nmkj-gsb__note">Already in your cart</p>' : ''}
      <div class="nmkj-gsb__actions"><button type="button" class="nmkj-gsb__btn" data-act="open" data-slot="${slot.id}" data-swap="${esc(e.sku)}">Swap</button>${rmv}</div></div></article>`;
  }
  watchHTML(slotId, ctx) {
    const label = this.R.STACK_SLOTS.find((s) => s.id === slotId).label.toLowerCase();
    const id = `nmkj-gsb-email-${slotId}-${ctx}`;
    return `<div class="nmkj-gsb__watch" data-gsb-watch>
      <button type="button" class="nmkj-gsb__btn nmkj-gsb__btn--link" data-act="watch-open" aria-expanded="false">Tell me when a compatible ${esc(label)} arrives</button>
      <form class="nmkj-gsb__watch-form" data-gsb-watch-form data-slot="${slotId}" novalidate hidden>
        <p class="nmkj-gsb__watch-lead">We'll email you once, when a compatible ${esc(label)} comes in.</p>
        <div class="nmkj-gsb__watch-row"><label class="visually-hidden" for="${id}">Email</label>
          <input id="${id}" type="email" name="email" autocomplete="email" inputmode="email" placeholder="Your email" required value="${esc(this.dataset.email || '')}">
          <button type="submit" class="nmkj-gsb__watch-submit">Watch for me</button></div>
        <div class="nmkj-gsb__watch-hp" aria-hidden="true"><label>Website<input type="text" name="website" tabindex="-1" autocomplete="off"></label></div>
        <p class="nmkj-gsb__watch-consent" data-consent>Email me when a compatible ${esc(label)} arrives at CORNER. Nothing else.</p>
        <p class="nmkj-gsb__watch-error" role="alert" hidden></p>
      </form><p class="nmkj-gsb__watch-done" role="status" hidden></p></div>`;
  }

  // ── Picker ─────────────────────────────────────────────────────────────────────────────
  openPicker(slotId, swapSku, opener) {
    const slot = this.R.STACK_SLOTS.find((s) => s.id === slotId);
    this.picker = { slotId, swapSku };
    this.returnFocus = opener;
    const all = this.candidates(slotId, swapSku);
    const feather = slot.roles.includes('feather');
    const sided = feather ? all.filter((c) => c.p.side) : all;
    const unsided = feather ? all.filter((c) => !c.p.side) : [];
    const grid = (list, tagSide) => `<div class="nmkj-gsb__grid">${list.map((c) => this.cardHTML(c, tagSide)).join('')}</div>`;
    let body;
    if (!all.length) body = `<div class="nmkj-gsb__dialog-empty"><p class="nmkj-gsb__empty-note">Nothing suitable is in stock right now.</p>${this.watchHTML(slotId, 'dialog')}</div>`;
    else body = (sided.length ? grid(sided, false) : '') + (unsided.length ? `<h3 class="nmkj-gsb__group-title">Side not recorded</h3>${grid(unsided, true)}` : '');
    this.dialog.innerHTML = `<div class="nmkj-gsb__dialog-inner"><div class="nmkj-gsb__dialog-head">
      <h2 class="nmkj-gsb__dialog-title" id="nmkj-gsb-dialog-title">${swapSku ? 'Swap for another' : 'Add a compatible'} ${esc(slot.label.toLowerCase())}</h2>
      <button type="button" class="nmkj-gsb__close" data-act="close" aria-label="Close">&times;</button></div>${body}</div>`;
    this.dialog.showModal();
    this.dialog.scrollTop = 0;
  }
  cardHTML(c, tagSide) {
    const p = c.p;
    const meta = window.nmkjCardMeta ? window.nmkjCardMeta(p.tags, 'nmkj-rec-card__meta', true) : '';
    const reasons = tagSide ? ['Side not recorded', ...c.reasons].slice(0, 2) : c.reasons;
    return `<article class="nmkj-rec-card nmkj-gsb__card" data-pick="${esc(p.sku)}">
      <div class="nmkj-rec-card__media-wrap"><div class="nmkj-rec-card__media">${p.image ? `<img class="nmkj-rec-card__image" src="${esc(p.image)}${p.image.includes('?') ? '&' : '?'}width=600" alt="" loading="lazy" width="600" height="600">` : ''}</div></div>
      <div class="nmkj-rec-card__info"><p class="nmkj-rec-card__brand">Goro's</p><p class="nmkj-rec-card__title">${esc(p.title)}</p>${meta}
      <p class="nmkj-rec-card__price">${money(p.price)}</p><p class="nmkj-gsb__reasons">${esc(reasons.join(' · '))}</p>
      <button type="button" class="nmkj-gsb__btn nmkj-gsb__pick" data-act="pick" data-sku="${esc(p.sku)}" aria-label="Choose ${esc(p.title)}">Choose</button></div></article>`;
  }
  pick(sku) {
    const { slotId, swapSku } = this.picker;
    const slot = this.R.STACK_SLOTS.find((s) => s.id === slotId);
    const p = this.feed.get(sku);
    const a = this.R.allowed(this.stackFor(swapSku), p, slotId);
    if (!a.ok) { this.setStatus(a.why, 'warn'); return; }
    if (swapSku) this.state.slots[slotId] = this.state.slots[slotId].filter((e) => e.sku !== swapSku);
    this.gone.delete(swapSku);
    this.state.slots[slotId].push({ sku, title: p.title });
    this.pendingRemaining = 0; // the stack changed: "add the remaining N" no longer counts anything real
    this.save();
    track('stack_slot_fill', { role: p.primary, slot: slotId, mode: swapSku ? 'swap' : 'new', sku });
    this.setStatus(null);
    this.announce(`${p.title} added as ${slot.label.toLowerCase()}.`);
    this.dialog.close();
    this.render();
    this.scrollToNextOpen();
  }
  onDialogClose() {
    this.dialog.innerHTML = '';
    const f = this.returnFocus;
    this.returnFocus = null;
    const again = f && f.dataset ? this.root.querySelector(`[data-act="open"][data-slot="${f.dataset.slot}"]`) : null;
    (again || this.root.querySelector('.nmkj-gsb__add') || this.root).focus?.();
  }

  // ── Events ─────────────────────────────────────────────────────────────────────────────
  onClick(e) {
    const card = e.target.closest('[data-pick]');
    const btn = e.target.closest('[data-act]');
    if (!btn && card && this.dialog.open) { this.pick(card.dataset.pick); return; }
    if (!btn) return;
    const act = btn.dataset.act;
    if (act === 'open') this.openPicker(btn.dataset.slot, btn.dataset.swap || null, btn);
    else if (act === 'close') this.dialog.close();
    else if (act === 'pick') this.pick(btn.dataset.sku);
    else if (act === 'remove') this.remove(btn.dataset.slot, btn.dataset.sku);
    else if (act === 'place') { if (this.placeFrom(btn.dataset.slot, btn.dataset.sku, true)) { this.entry = null; this.render(); this.scrollToNextOpen(); } else this.render(); }
    else if (act === 'entry-dismiss') { this.entry = null; this.render(); }
    else if (act === 'watch-open') { const f = btn.nextElementSibling; f.hidden = !f.hidden; btn.setAttribute('aria-expanded', String(!f.hidden)); if (!f.hidden) f.querySelector('input[type=email]').focus(); }
    else if (act === 'add-all') this.addAll(false);
    else if (act === 'add-remaining') this.addAll(true);
  }
  onChange(e) {
    if (e.target.dataset.act !== 'owned-base') return;
    this.state.ownedBase = e.target.checked;
    if (this.state.ownedBase) this.state.slots.base = [];
    else this.state.slots.hook = [];
    this.pendingRemaining = 0; // the stack changed: "add the remaining N" no longer counts anything real
    this.save();
    this.announce(this.state.ownedBase ? 'Using your own chain and hook. A hook slot is now available.' : 'Chain and hook slot restored.');
    this.render();
    this.root.querySelector('[data-act="owned-base"]')?.focus();
  }
  remove(slotId, sku) {
    const e = this.state.slots[slotId].find((x) => x.sku === sku);
    this.state.slots[slotId] = this.state.slots[slotId].filter((x) => x.sku !== sku);
    this.gone.delete(sku);
    this.pendingRemaining = 0; // the stack changed: "add the remaining N" no longer counts anything real
    this.save();
    this.announce(`${(e && e.title) || 'Piece'} removed.`);
    this.render();
    this.root.querySelector(`[data-slot="${slotId}"] .nmkj-gsb__add, [data-slot="${slotId}"] button`)?.focus();
  }
  setStatus(text, kind) {
    this.status = text ? { text, kind: kind || 'info' } : null;
    const el = this.root.querySelector('[data-gsb-status]');
    if (el) { el.textContent = text || ''; el.classList.toggle('nmkj-gsb__status--warn', kind === 'warn'); }
    // The phone bar carries the warning too — several paths call setStatus AFTER render(), and a
    // customer adding from the bar must see "a piece just sold" without scrolling to the card.
    const bar = this.root.querySelector('[data-gsb-bar]');
    if (bar) {
      let w = bar.querySelector('.nmkj-gsb__bar-warn');
      if (kind === 'warn' && text) {
        if (!w) { w = document.createElement('p'); w.className = 'nmkj-gsb__bar-warn'; bar.prepend(w); }
        w.textContent = text;
      } else if (w) w.remove();
    }
    if (text) this.announce(text);
  }
  announce(text) {
    this.live.textContent = '';
    setTimeout(() => { this.live.textContent = text; }, 50);
  }

  // ── Add to cart ────────────────────────────────────────────────────────────────────────
  async addAll(skipGone) {
    const btns = this.root.querySelectorAll('[data-act="add-all"], [data-act="add-remaining"]');
    btns.forEach((b) => { b.disabled = true; });
    this.setStatus('Checking availability…');
    await Promise.all([this.refresh(), this.loadCart()]);
    const goneNow = this.entries().filter((e) => this.gone.has(e.sku));
    const chosen = this.chosen();
    const items = chosen.filter((e) => !this.cartIds.has(e.piece.variantId));
    if (goneNow.length && !skipGone) {
      this.pendingRemaining = items.length;
      this.render();
      this.setStatus(`${goneNow.length === 1 ? 'A piece in your set-up has' : `${goneNow.length} pieces in your set-up have`} just sold. Nothing was added — replace or remove ${goneNow.length === 1 ? 'it' : 'them'}, or add the rest.`, 'warn');
      return;
    }
    this.pendingRemaining = 0;
    if (!items.length) { this.render(); this.setStatus(chosen.length ? 'Everything in your set-up is already in your cart.' : 'Choose at least one piece first.'); return; }
    const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean).join(',');
    let json = null; let status = 0;
    try {
      const r = await fetch(this.dataset.cartAdd, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items.map((e) => ({ id: e.piece.variantId, quantity: 1, properties: { _stack: 'goros-stack-builder' } })), sections }),
      });
      status = r.status;
      json = await r.json().catch(() => null);
    } catch { status = 0; }
    if (status !== 200 || !json || json.status) {
      // Shopify's multi-item add is all-or-nothing: one dead variant → 422 and nothing added.
      // Re-check so the slot that sold in the last second is marked, then offer the rest.
      await Promise.all([this.refresh(), this.loadCart()]);
      const stillGone = this.entries().filter((e) => this.gone.has(e.sku)).length;
      const remaining = this.chosen().filter((e) => !this.cartIds.has(e.piece.variantId)).length;
      this.pendingRemaining = stillGone && remaining ? remaining : 0;
      this.render();
      this.setStatus(stillGone ? 'A piece just sold, so nothing was added. Replace or remove it, or add the rest.' : 'We could not add these to your cart — a piece may have just sold. Nothing was added; please try again.', 'warn');
      return;
    }
    await this.loadCart();
    this.render();
    const n = (json.items || items).length;
    this.setStatus(`${n} ${n === 1 ? 'piece' : 'pieces'} added to your cart.`);
    track('stack_add_to_cart', {
      items: n, value: Number(items.reduce((s, e) => s + e.piece.price, 0).toFixed(2)),
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      partial: Boolean(skipGone), skipped_in_cart: chosen.length - items.length,
    });
    const mod = await this.events;
    // cart-icon.js SETS the bubble to itemCount unless the source is the product form, so this is the cart's total.
    const data = { source: 'nmkj-goros-stack', itemCount: this.cartCount || n, sections: json.sections };
    const ev = mod && mod.CartAddEvent ? new mod.CartAddEvent(json, 'nmkj-goros-stack', data)
      : new CustomEvent('cart:update', { bubbles: true, detail: { resource: json, sourceId: 'nmkj-goros-stack', data } });
    this.dispatchEvent(ev);
  }

  // ── Watch ──────────────────────────────────────────────────────────────────────────────
  async onSubmit(e) {
    const form = e.target.closest('[data-gsb-watch-form]');
    if (!form) return;
    e.preventDefault();
    const err = form.querySelector('[role="alert"]');
    const submit = form.querySelector('.nmkj-gsb__watch-submit');
    const fail = (msg) => { err.textContent = msg; err.hidden = false; submit.disabled = false; submit.textContent = 'Watch for me'; };
    err.hidden = true;
    const email = String(form.email.value || '').trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { fail('Please enter your email address.'); return; }
    submit.disabled = true; submit.textContent = 'One moment…';
    const slotId = form.dataset.slot;
    const chosen = this.chosen();
    const wishlist = (window.nmkjWishlist && (window.nmkjWishlist.getPieces || window.nmkjWishlist.getItems)) ? (window.nmkjWishlist.getPieces || window.nmkjWishlist.getItems)() : [];
    const pieces = [...chosen.map((c) => ({ sku: c.piece.sku, handle: c.piece.handle, title: c.piece.title, price: c.piece.price, vendor: c.piece.vendor })), ...wishlist];
    const want = { role: WANT_ROLE[slotId] };
    const slot = this.R.STACK_SLOTS.find((s) => s.id === slotId);
    if (slot.side) want.side = slot.side;
    const metals = new Set(chosen.flatMap((c) => [...c.piece.metal]));
    if (metals.has('Gold') && metals.has('Silver')) want.metal = 'Gold+Silver';
    else if (metals.has('Gold')) want.metal = 'Gold';
    else if (metals.has('Silver')) want.metal = 'Silver';
    const other = slot.roles.includes('feather') ? chosen.find((c) => c.piece.primary === 'feather' && c.slot !== slotId) : null;
    if (other) { if (other.piece.size) want.size_class = other.piece.size; if (other.piece.design) want.design = other.piece.design; }
    else {
      const sizes = {}; chosen.forEach((c) => { if (c.piece.size) sizes[c.piece.size] = (sizes[c.piece.size] || 0) + 1; });
      const top = Object.entries(sizes).sort((a, b) => b[1] - a[1])[0];
      if (top) want.size_class = top[0];
    }
    const payload = { email, locale: locale(), consent_text: form.querySelector('[data-consent]').textContent.trim(), source_url: location.href, pieces, want, website: form.website.value || '' };
    try {
      const r = await fetch(this.dataset.intake.replace(/\/$/, '') + '/watch', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
      const j = await r.json().catch(() => null);
      if (!j || !j.ok) { fail((j && j.error) || 'We could not save that. Please email info@cornerusa.com.'); return; }
      const done = form.parentElement.querySelector('.nmkj-gsb__watch-done');
      done.textContent = `We'll email you when ${j.summary ? String(j.summary) : `a compatible ${slot.label.toLowerCase()}`} arrives.`;
      form.hidden = true; done.hidden = false;
      form.parentElement.querySelector('[data-act="watch-open"]').hidden = true;
      const w = { role: want.role }; if (want.side) w.side = want.side; if (want.size_class) w.size_class = want.size_class;
      track('stack_watch_create', w); // nothing personal: no email, no pieces
      this.announce(done.textContent);
    } catch { fail('We could not reach the team. Please email info@cornerusa.com.'); }
  }
}

if (!customElements.get('nmkj-goros-stack')) customElements.define('nmkj-goros-stack', NmkjGorosStack);
